import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "./Logo";
import { scrollToId } from "./Reveal";
import { useLang, LangSwitch } from "../../i18n";

const LINKS = ["avantatges", "seguretat", "mides", "com-funciona"];

const MobileMenu = ({ open, onGo, t }) => (
  <AnimatePresence>
    {open && (
      <motion.nav
        data-testid="mobile-menu"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.3 }}
        className="absolute inset-x-3 top-full mt-2 rounded-3xl border border-white/10 bg-[#1f1f1f]/95 p-3 shadow-2xl backdrop-blur-xl md:hidden"
      >
        <div className="flex items-center justify-between px-5 pb-2 pt-1">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#B5ACA2]">{t.nav.language}</span>
          <LangSwitch />
        </div>
        {[...LINKS, "reserva"].map((id, i) => (
          <MenuItem key={id} id={id} i={i} label={id === "reserva" ? t.nav.mobileReserve : t.nav[id]} onGo={onGo} />
        ))}
      </motion.nav>
    )}
  </AnimatePresence>
);

const MenuItem = ({ id, i, label, onGo }) => (
          <button
            onClick={() => onGo(id)}
            data-testid={`mobile-nav-link-${id}`}
            className={`flex w-full items-center justify-between rounded-2xl px-5 py-4 text-left text-base font-bold ${
              id === "reserva" ? "mt-2 bg-[#F26D5A] text-[#1f1f1f]" : "text-[#FDFBF7] hover:bg-white/5"
            }`}
          >
            {label}
            <span className={`text-xs ${id === "reserva" ? "text-[#1f1f1f]" : "text-[#A69B8F]"}`}>0{i + 1}</span>
          </button>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  const go = (id) => { setOpen(false); scrollToId(id); };
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
        scrolled || open ? "bg-[#1f1f1f]/85 py-3 shadow-[0_10px_40px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl" : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
        <button onClick={() => scrollToId("inici")} data-testid="nav-logo-btn" aria-label={t.nav.home}>
          <Logo dark tagline={false} />
        </button>
        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((id) => (
            <button
              key={id}
              onClick={() => scrollToId(id)}
              data-testid={`nav-link-${id}`}
              className="link-underline text-sm font-semibold text-[#EDE6DC] hover:text-white"
            >
              {t.nav[id]}
            </button>
          ))}
          <LangSwitch />
        </nav>
        <div className="flex items-center gap-2">
          <button
            onClick={() => go("reserva")}
            data-testid="nav-reserve-btn"
            className="btn-magnet rounded-full bg-[#F26D5A] px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#1f1f1f] sm:px-6 sm:text-sm"
          >
            {t.nav.reserve}
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label={open ? t.nav.close : t.nav.open}
            aria-expanded={open}
            className="grid h-10 w-10 place-items-center rounded-full border border-white/20 text-[#FDFBF7] md:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <MobileMenu open={open} onGo={go} t={t} />
    </header>
  );
};
